import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { isProtected } from '../../utils/protection';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { AlertRow } from '../shell/AlertRow';
import { StatusBanner } from '../shell/StatusBanner';
import { DamageTierSelector } from './DamageTierSelector';
import { THIEVERY_OPERATIONS } from '../../constants/thievery';
import { ThieveryResult as ThieveryResultComponent } from '../ThieveryResult';
import type { ThieveryShellProps } from '../../pages/Thievery';
import type { ThieveryOperationType } from '../../types/thievery';

const selectStyle = { ...INPUT_STYLE, border: `1px solid ${COLORS.inputBorder}` } as const;

/**
 * SF-149: mobile card-based content for Thievery — 3 cards (Your Thieves / Target / Operation),
 * Commit's preview summary/disabled-reason/submit button folded into the end of "Operation"
 * (Phase 3 Decision 1). No sticky bottom bar — "Conduct Operation" is a single decision point,
 * not an accumulating multi-row order (Key Implementation Note 3).
 */
export function MobileThieveryShell({
  province,
  thiefData,
  tpaStrength,
  hasNoThieves,
  kingdomNumber,
  setKingdomNumber,
  islandNumber,
  setIslandNumber,
  handleChangeKingdom,
  isLoadingKingdom,
  kingdomMembers,
  selectedTarget,
  handleSelectTarget,
  selectedTargetProtectionCountdown,
  selectedOperation,
  setSelectedOperation,
  operation,
  isIntel,
  damageTier,
  setDamageTier,
  allowedTiers,
  tierLabels,
  thievesSentPreview,
  operationInfo,
  submitDisabledReason,
  isSubmitting,
  handleConductOperation,
  operationResult,
}: ThieveryShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  // SF-149 Phase 3 Decision 6: post-submit result reachability — imperative focus move on the
  // result container when it transitions from null to set, mirroring the <h1> focus convention.
  useEffect(() => {
    if (operationResult) {
      resultRef.current?.focus();
    }
  }, [operationResult]);

  return (
    <div className="space-y-3">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Thievery
      </h1>

      {hasNoThieves && (
        <AlertRow
          severity="warning"
          message="You have no thieves. Train thieves in the Military page to conduct operations."
        />
      )}

      {operationResult && (
        <div ref={resultRef} tabIndex={-1} className="outline-none">
          <ThieveryResultComponent result={operationResult} />
        </div>
      )}

      <GlanceCard title="Your Thieves">
        <MetricRow label="Thieves" value={formatNumber(thiefData.thieves)} unit="" weight="primary" />
        <MetricRow label="TPA" value={thiefData.tpa.toFixed(2)} unit="" weight="secondary" />
        <div className="text-[11px] -mt-1.5" style={{ color: COLORS.text.tertiary }}>
          {tpaStrength.label}
        </div>
        <MetricRow label="Stealth" value={thiefData.stealth.toFixed(0)} unit="%" weight="secondary" />
      </GlanceCard>

      <GlanceCard title="Target">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm w-16 flex-shrink-0" style={{ color: COLORS.text.secondary }}>Kingdom</span>
            <input
              type="number"
              placeholder="14"
              value={kingdomNumber}
              onChange={(e) => setKingdomNumber(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="flex-1 rounded text-sm px-2"
              style={{ ...selectStyle, minHeight: 44 }}
              disabled={hasNoThieves}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm w-16 flex-shrink-0" style={{ color: COLORS.text.secondary }}>Island</span>
            <input
              type="number"
              placeholder="40"
              value={islandNumber}
              onChange={(e) => setIslandNumber(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="flex-1 rounded text-sm px-2"
              style={{ ...selectStyle, minHeight: 44 }}
              disabled={hasNoThieves}
            />
          </div>
          <button
            onClick={handleChangeKingdom}
            disabled={isLoadingKingdom || hasNoThieves}
            className="w-full text-center py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{
              minHeight: 44,
              border: `1px solid ${COLORS.link}`,
              color: COLORS.link,
              background: 'none',
            }}
          >
            {isLoadingKingdom ? 'Loading...' : 'Change Kingdom'}
          </button>
        </div>

        {kingdomMembers.length === 0 && !isLoadingKingdom ? (
          <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
            No members found in this kingdom.
          </div>
        ) : (
          <select
            value={selectedTarget}
            onChange={(e) => handleSelectTarget(e.target.value)}
            className="w-full rounded text-sm py-2 px-2"
            style={{ ...selectStyle, minHeight: 44 }}
            disabled={hasNoThieves}
            aria-describedby={selectedTargetProtectionCountdown ? 'target-protection-status' : undefined}
          >
            <option value="">Select a target...</option>
            {kingdomMembers.map((member) => {
              const memberProtected = isProtected(member.protectionExpiresAtTick, province?.sync.currentTick ?? 0);
              return (
                <option key={member.provinceId} value={member.provinceId} disabled={memberProtected}>
                  {member.provinceName} ({member.location}){memberProtected ? ' (Protected)' : ''}
                </option>
              );
            })}
          </select>
        )}

        {selectedTargetProtectionCountdown && (
          <div id="target-protection-status">
            <StatusBanner
              tone="protection"
              icon="🛡️"
              headline="Target is protected"
              detail={selectedTargetProtectionCountdown}
              variant="full"
            />
          </div>
        )}
      </GlanceCard>

      <GlanceCard title="Operation">
        <select
          value={selectedOperation}
          onChange={(e) => setSelectedOperation(e.target.value as ThieveryOperationType)}
          className="w-full rounded text-sm py-2 px-2"
          style={{ ...selectStyle, minHeight: 44 }}
          disabled={!selectedTarget || hasNoThieves}
        >
          <option value="">Select an operation...</option>
          <optgroup label="Intelligence">
            {THIEVERY_OPERATIONS.filter((op) => op.category === 'INTEL').map((op) => (
              <option key={op.type} value={op.type}>{op.name}</option>
            ))}
          </optgroup>
          <optgroup label="Resource Theft">
            {THIEVERY_OPERATIONS.filter((op) => op.category === 'RESOURCE').map((op) => (
              <option key={op.type} value={op.type}>{op.name}</option>
            ))}
          </optgroup>
          <optgroup label="Destructive">
            {THIEVERY_OPERATIONS.filter((op) => op.category === 'DESTRUCTIVE').map((op) => (
              <option key={op.type} value={op.type}>{op.name}</option>
            ))}
          </optgroup>
          <optgroup label="Duration Effects">
            {THIEVERY_OPERATIONS.filter((op) => op.category === 'DURATION').map((op) => (
              <option key={op.type} value={op.type}>{op.name}</option>
            ))}
          </optgroup>
        </select>

        {operation && (
          isIntel ? (
            <div className="text-sm" style={{ color: COLORS.text.secondary }}>
              Auto-commit: {formatNumber(thievesSentPreview)} thieves (
              {(operation.thiefCostPercent * 100).toFixed(0)}% of {formatNumber(thiefData.thieves)})
            </div>
          ) : (
            <div>
              <div id="damage-tier-label" className="text-sm mb-1" style={{ color: COLORS.text.secondary }}>
                {operation.category === 'RESOURCE' ? 'Operation Scale'
                  : operation.category === 'DURATION' ? 'Operation Intensity'
                  : 'Damage Tier'}
              </div>
              <DamageTierSelector
                value={damageTier}
                onChange={setDamageTier}
                allowedTiers={allowedTiers}
                labels={tierLabels}
              />
            </div>
          )
        )}

        {operationInfo && (
          <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
            {operationInfo.operation.description}
          </p>
        )}

        <div className="flex flex-col gap-1 text-sm">
          <div className="flex items-center justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Sending</span>
            <span className="font-mono" style={{ color: thievesSentPreview > 0 ? COLORS.text.primary : COLORS.text.tertiary }}>
              {formatNumber(thievesSentPreview)} thieves
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Stealth Cost</span>
            <span className="font-mono" style={{ color: operationInfo ? COLORS.text.primary : COLORS.text.tertiary }}>
              {operationInfo ? `~${Math.round(operationInfo.stealthCost)}%` : '—'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Success</span>
            <span className="font-mono" style={{ color: operationInfo ? COLORS.text.primary : COLORS.text.tertiary }}>
              {operationInfo ? `~${operationInfo.successRate}%` : '—'}
            </span>
          </div>
          {operationInfo && operationInfo.estLoss > 0 && (
            <div className="flex items-center justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Est. Loss on Failure</span>
              <span className="font-mono" style={{ color: COLORS.text.primary }}>
                ~{formatNumber(operationInfo.estLoss)} thieves
              </span>
            </div>
          )}
        </div>

        {submitDisabledReason && (
          <div className="text-xs" style={{ color: COLORS.warning }}>
            {submitDisabledReason}
          </div>
        )}

        <button
          onClick={handleConductOperation}
          disabled={isSubmitting || !!submitDisabledReason}
          className="w-full text-center py-2.5 rounded-lg text-sm font-bold disabled:opacity-50"
          style={{
            minHeight: 44,
            backgroundColor: COLORS.btnPrimary,
            border: `1px solid ${COLORS.btnPrimaryHover}`,
            color: COLORS.text.primary,
          }}
        >
          {isSubmitting ? 'Sending…' : 'Conduct Operation'}
        </button>
      </GlanceCard>
    </div>
  );
}
